import React, { lazy, Suspense } from 'react';
import {
    BrowserRouter as Router,
    Route,
    Redirect,
    Switch
} from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
import Mousetrap from 'mousetrap';
import QuickSearchTrigger from './quicksearch-context';
import Head from '../../components/Head/Head';
import QuickSearch from '../../components/QuickSearch/QuickSearch';
import urlCheck from '../../utils/urlCheck';
import WatchlistNotification from '../../components/WatchlistNotification/WatchlistNotification';
import { WatchlistNotificationProvider } from '../../components/WatchlistNotification/watchlistnotification-context';
import { WatchlistProvider } from '../../Hooks/useWatchlist';
import { WatchlistSearchProvider } from '../../Hooks/useWatchlistSearch';
import ReactTarget from '../../components/ReactTarget/ReactTarget';
import getConfig, { isLowerEnvironment } from '../../config';
import { withToast } from '../../components/Toast/withToast';
import { dismissBucketToast } from '../../components/AltBucketToast/util';
import VersionToast from '../../components/Toast/VersionToast';
import AltBucketToast from '../../components/AltBucketToast/AltBucketToast';
import { getCookie } from '../../utils/cookie';
import environment from '../../utils/environment';
import RouteIntercept from '../RouteIntercept/RouteIntercept';
import { RESEARCH_TAB_IDS } from '../../utils/constants';

const DirectLandingPage = lazy(() =>
    import('../../components/DirectLandingPage/DirectLandingPage')
);

const MutualFundResearchTool = lazy(() =>
    import('../MutualFundResearchTool/MutualFundResearchTool')
);

const ETFResearchTool = lazy(() =>
    import('../ETFResearchTool/ETFResearchTool')
);

const FundBook = lazy(() => import('../FundBook/FundBook'));
const Compare = lazy(() => import('../Compare/Compare'));
const Watchlist = lazy(() => import('../Watchlist/Watchlist'));

const MorningStar = lazy(() => import('../MorningStar/MorningStar'));

const hideHeader = '.pi-shared-nav-wrapper { display: none !important; }';

// const version = config('version');

const categoryLink = (link) => (
    <Route
        path={`/${link}`}
        render={() => (
            <Redirect
                to={{
                    pathname: '/',
                    state: {
                        linkedFrom: link
                    }
                }}
            />
        )}
    />
);

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            openQS: false,
            pushWatchlistNotification: false
        };
    }

    componentDidMount() {
        if (isLowerEnvironment(window.location.host)) {
            // bind toast to hotkey
            const bucketCookie = getCookie('stageval');
            if (bucketCookie) {
                this.renderBucketBannerToast();
            }

            Mousetrap.bind('shift+3', this.renderVersionToast);
            Mousetrap.bind('shift+4', this.renderBucketBannerToast);
        }
    }

    toggleModal = () => {
        const { openQS } = this.state;
        this.setState({ openQS: !openQS });
    };

    closeModal = () => {
        this.setState({ openQS: false });
    };

    appendPushClassOnNotification = () => {
        const { pushWatchlistNotification } = this.state;
        this.setState({
            pushWatchlistNotification: !pushWatchlistNotification
        });
    };

    showHeadersForAccountFinder = () => {
        if (window.location.href.indexOf('account-finder') > -1) return false;

        return true;
    };

    renderVersionToast = () =>
        this.props.addToast(<VersionToast />, {
            appearance: 'info',
            autoDismiss: true
        });

    renderBucketBannerToast = () => {
        this.props.addToast(<AltBucketToast />, {
            appearance: 'warning',
            autoDismiss: false,
            id: 'bucket-banner',
            onDismiss: dismissBucketToast
        });
    };

    render() {
        const { client, loggedIn, userStateLoaded } = this.props;
        const { pushWatchlistNotification } = this.state;

        return (
            // test-n-target for action bar multi buy - US240892
            <ReactTarget mbox="target-global-mbox" defaultExperience="a">
                <QuickSearchTrigger.Provider value={this.toggleModal}>
                    <WatchlistNotificationProvider>
                        <WatchlistProvider
                            loggedIn={loggedIn}
                            pushWatchlistNotification={
                                pushWatchlistNotification
                            }
                        >
                            <ApolloProvider client={client}>
                                <Router basename="/personal-investing/tools/fund-research/">
                                    <RouteIntercept>
                                        <Suspense fallback={false}>
                                            <div className="app">
                                                <Head />
                                                <div className="app-body">
                                                    <Switch>
                                                        <Route
                                                            exact
                                                            path="/morningstar"
                                                            render={() => (
                                                                <MorningStar
                                                                    title="Planning Tools Terms of Registration"
                                                                    description="Planning Tools Terms of Registration"
                                                                    loggedIn={
                                                                        loggedIn
                                                                    }
                                                                    userStateLoaded={
                                                                        userStateLoaded
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/watchlist"
                                                            render={(props) => (
                                                                <WatchlistSearchProvider
                                                                >
                                                                    <Watchlist
                                                                        {...props}
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                    />
                                                                </WatchlistSearchProvider>
                                                            )}
                                                        />

                                                        <Route
                                                            exact
                                                            path="/historical-performance"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        supressHeader
                                                                        tabFocus={
                                                                            3
                                                                        }
                                                                        sortDir="desc"
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/daily-prices"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        supressHeader
                                                                        tabFocus={
                                                                            2
                                                                        }
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        customStartTab={
                                                                            RESEARCH_TAB_IDS.PRICES
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        {/* DIRECT LINKS */}
                                                        <Route
                                                            exact
                                                            path="/stock-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/bond-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/money-market-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/iclass-shares"
                                                            render={() => (
                                                                <React.Fragment>
                                                                    {window.location.assign(
                                                                        `${getConfig(
                                                                            'envDomain'
                                                                        )}/personal-investing/funds/mutual-funds/iclass-shares.html`
                                                                    )}
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        {/* IIRT-350 - Hiding Closed funds while they are so few */}
                                                        <Route
                                                            exact
                                                            path="/closed-funds"
                                                            render={
                                                                (/* props */) => (
                                                                    <Redirect
                                                                        to={{
                                                                            pathname:
                                                                                '/'
                                                                        }}
                                                                    />
                                                                    // <React.Fragment>
                                                                    //     <div>
                                                                    //         <DirectLandingPage
                                                                    //             {...props}
                                                                    //             pathname={
                                                                    //                 props
                                                                    //                     .location
                                                                    //                     .pathname
                                                                    //             }
                                                                    //         />
                                                                    //     </div>
                                                                    //     <MutualFundResearchTool
                                                                    //         directLink
                                                                    //         loggedIn={
                                                                    //             loggedIn
                                                                    //         }
                                                                    //         userStateLoaded={
                                                                    //             userStateLoaded
                                                                    //         }
                                                                    //         togglePushClass={
                                                                    //             this
                                                                    //                 .appendPushClassOnNotification
                                                                    //         }
                                                                    //         {...props}
                                                                    //     />
                                                                    // </React.Fragment>
                                                                )
                                                            }
                                                        />
                                                        <Route
                                                            exact
                                                            path="/asset-allocation-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/morningstar-4-and-5-star-rated-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/msf"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <MutualFundResearchTool
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/target-date-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                        hashlink={
                                                                            props
                                                                                .location
                                                                                .hash
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/select-funds"
                                                            render={(props) => (
                                                                <React.Fragment>
                                                                    <DirectLandingPage
                                                                        {...props}
                                                                        pathname={
                                                                            props
                                                                                .location
                                                                                .pathname
                                                                        }
                                                                    />
                                                                    <MutualFundResearchTool
                                                                        directLink
                                                                        loggedIn={
                                                                            loggedIn
                                                                        }
                                                                        userStateLoaded={
                                                                            userStateLoaded
                                                                        }
                                                                        togglePushClass={
                                                                            this
                                                                                .appendPushClassOnNotification
                                                                        }
                                                                        {...props}
                                                                    />
                                                                </React.Fragment>
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/etf"
                                                            render={() => (
                                                                <ETFResearchTool />
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/"
                                                            render={(props) => (
                                                                <MutualFundResearchTool
                                                                    {...props}
                                                                    loggedIn={
                                                                        loggedIn
                                                                    }
                                                                    userStateLoaded={
                                                                        userStateLoaded
                                                                    }
                                                                    togglePushClass={
                                                                        this
                                                                            .appendPushClassOnNotification
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Route
                                                            exact
                                                            path="/compare"
                                                            render={() => (
                                                                <Compare
                                                                    loggedIn={
                                                                        loggedIn
                                                                    }
                                                                    userStateLoaded={
                                                                        userStateLoaded
                                                                    }
                                                                    appendPushClassOnNotification={
                                                                        this
                                                                            .appendPushClassOnNotification
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        {categoryLink('bnfdns')}
                                                        {categoryLink('stfnds')}
                                                        {categoryLink('msf')}
                                                        {categoryLink('aaf')}
                                                        {categoryLink('tdf')}
                                                        {categoryLink(
                                                            'alternative'
                                                        )}
                                                        {categoryLink(
                                                            'international-stocks'
                                                        )}
                                                        {categoryLink(
                                                            'municipal-bond'
                                                        )}
                                                        {categoryLink(
                                                            'sector-equity'
                                                        )}
                                                        {categoryLink(
                                                            'taxable-bond'
                                                        )}
                                                        {categoryLink(
                                                            'us-equity'
                                                        )}
                                                        {categoryLink(
                                                            'investor-class'
                                                        )}
                                                        {categoryLink('iclass')}
                                                        {categoryLink(
                                                            'open-to-new-investors'
                                                        )}
                                                        {categoryLink('mmfnds')}
                                                        <Route
                                                            path="/etf/:fund"
                                                            component={(
                                                                props
                                                            ) => (
                                                                <FundBook
                                                                    {...props}
                                                                    etf
                                                                />
                                                            )}
                                                        />
                                                        <Route
                                                            path="/:fund"
                                                            render={(props) => (
                                                                <FundBook
                                                                    {...props}
                                                                    loggedIn={
                                                                        loggedIn
                                                                    }
                                                                    userStateLoaded={
                                                                        userStateLoaded
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Switch>
                                                    <QuickSearch
                                                        closeModal={
                                                            this.closeModal
                                                        }
                                                        toggle={
                                                            this.toggleModal
                                                        }
                                                        open={this.state.openQS}
                                                    />
                                                    <WatchlistNotification />
                                                    {!urlCheck('mobile') && (
                                                        <div className="beacon-theme-modern-outlook beacon-on-darker">
                                                            <ii-footer
                                                                id="mastfooter"
                                                                run-mode="publish"
                                                                environment={
                                                                    environment
                                                                }
                                                                disclaimer-and-copyright-path="/content/iinvestor/en/disclosures/pi-footer-disclosures.model.json"
                                                            />
                                                        </div>
                                                    )}
                                                    {urlCheck('mobile') && (
                                                        <style>
                                                            {hideHeader}
                                                        </style>
                                                    )}
                                                </div>
                                            </div>
                                        </Suspense>
                                    </RouteIntercept>
                                </Router>
                            </ApolloProvider>
                        </WatchlistProvider>
                    </WatchlistNotificationProvider>
                </QuickSearchTrigger.Provider>
            </ReactTarget>
        );
    }
}

export default withToast(App);
